import { useAudioTrack, useDaily, useLocalSessionId } from '@daily-co/daily-react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { shouldPlayerBeMuted } from '../utils/muteRules';
import useParticipantsWithHost from './useParticipantsWithHost';
import useWSTurnState from './useWSTurnState';

export default function useMuteState() {
  const callObject = useDaily();
  const localSessionId = useLocalSessionId();
  const localAudio = useAudioTrack(localSessionId);
  const isLocalAudioOff = localAudio?.isOff ?? false;

  const systemMutedRef = useRef(false);
  const manualMuteBeforeSystemRef = useRef(false);

  // Hooks para lógica de mute por turn state
  const { currentPhase, lastCompleter } = useWSTurnState();
  const { whoAmI } = useParticipantsWithHost();

  // Estado atual do mute manual
  const isManuallyMuted = isLocalAudioOff && !systemMutedRef.current;

  // Lógica de mute por turn state usando a função shouldPlayerBeMuted
  const isMutedByTurnState = useMemo(() => {
    // Se não temos whoAmI, não mutamos por turn state
    if (!whoAmI) return false;

    const currentState = {
      phase: currentPhase,
      lastCompleter
    };

    return shouldPlayerBeMuted(currentState, whoAmI);
  }, [currentPhase, lastCompleter, whoAmI]);

  // Estado combinado: mute manual OU mute por turn state
  const isMuted = isManuallyMuted || isMutedByTurnState;

  // Aplicar mute/unmute no Daily quando isMutedByTurnState mudar
  useEffect(() => {
    if (!callObject) return;

    if (isMutedByTurnState) {
      if (!systemMutedRef.current) {
        manualMuteBeforeSystemRef.current = isLocalAudioOff;
        systemMutedRef.current = true;
      }
      callObject.setLocalAudio(false); // força mute
    } else if (systemMutedRef.current) {
      systemMutedRef.current = false;
      if (!manualMuteBeforeSystemRef.current) {
        callObject.setLocalAudio(true); // libera se não estiver manualmente mutado
      }
      manualMuteBeforeSystemRef.current = false;
    }
  }, [callObject, isMutedByTurnState, isLocalAudioOff]);

  // Função de toggle (só funciona para mute manual e se não estiver bloqueado pelo turno)
  const toggleMute = useCallback(() => {
    if (!isMutedByTurnState && !systemMutedRef.current) {
      callObject.setLocalAudio(isManuallyMuted);
    }
  }, [callObject, isManuallyMuted, isMutedByTurnState]);

  // Determinar o motivo do mute
  const muteReason = useMemo(() => {
    if (isManuallyMuted && isMutedByTurnState) return 'BOTH';
    if (isManuallyMuted) return 'MANUAL';
    if (isMutedByTurnState) return 'TURN_STATE';
    return 'NONE';
  }, [isManuallyMuted, isMutedByTurnState]);

  return {
    isMuted,
    isManuallyMuted,
    isMutedByTurnState,
    muteReason,
    toggleMute,
    canToggleMute: !isMutedByTurnState, // Só pode toggle se não estiver mutado pelo sistema
  };
}
